// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button, Drawer } from '@imprivata-cloud/design-system';
import { downloadFromUrl, formatDateInUtcWithTz } from '@utils/utils';
import { Spin, message } from 'antd';
import { Copy, Download, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAgentInstaller } from '../hooks/useAgentInstaller';
import { installCommandLine } from '../utils/installCommandLine';

const TRANSLATION_ROOT = 'portal.settings.endpoints.installer-drawer';
// Figma shows bullets first, then a short suffix of the key; the rest stays
// masked (REQ-033).
const KEY_PREVIEW_LENGTH = 6;
const KEY_MASK = '••••••••••••••••••••••';

type AgentInstallerDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const maskKey = (key: string) =>
  key.length <= KEY_PREVIEW_LENGTH ? KEY_MASK : `${KEY_MASK}${key.slice(-KEY_PREVIEW_LENGTH)}`;

/**
 * Agent-settings drawer (Figma 27078-844). Installer data (version + MSI URL)
 * loads on open. The installation key is generated lazily — nothing is fetched
 * until the admin clicks "Generate key" — and the decrypted plaintext is held
 * in memory only, shown partially masked, and copied via the clipboard; it is
 * never logged or fully rendered (REQ-033).
 */
export const AgentInstallerDrawer = ({ open, onClose }: AgentInstallerDrawerProps) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const { installerData, installationKey } = useAgentInstaller(open);

  const copy = (text: string, successKey: string) => {
    navigator.clipboard.writeText(text).then(
      () => messageApi.success(t(successKey)),
      // Surface clipboard failures (insecure context, denied permission) instead
      // of silently doing nothing (no-silent-failures).
      () => messageApi.error(t(`${TRANSLATION_ROOT}.copy-error`)),
    );
  };

  // Discard the decrypted key when the drawer closes so the plaintext bearer
  // credential does not linger in memory or reappear on reopen (REQ-033).
  const handleClose = () => {
    installationKey.reset();
    onClose();
  };

  const key = installationKey.data?.key;
  const version = installerData.data?.version ?? '';

  return (
    <Drawer open={open} onOpenChange={(next: boolean) => !next && handleClose()}>
      <Drawer.Panel title={t(`${TRANSLATION_ROOT}.title`)} width="690px">
        {contextHolder}

        <div className="endpoints-installer-region">
          {installerData.isLoading && <Spin />}
          {installerData.isError && (
            <p className="endpoints-drawer-error">{t(`${TRANSLATION_ROOT}.installer-error`)}</p>
          )}
          {installerData.data && (
            <>
              <span className="endpoints-field-label">
                {t(`${TRANSLATION_ROOT}.version-label`)}
              </span>
              {installerData.data.version && <span>{installerData.data.version}</span>}
              <div className="endpoints-installer-row">
                <span>{t(`${TRANSLATION_ROOT}.download-msi-label`)}</span>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Download size={16} />}
                  onClick={() => downloadFromUrl(installerData.data.endpointInstallerUrl)}
                >
                  {t(`${TRANSLATION_ROOT}.download-button`)}
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="endpoints-key-region">
          <span className="endpoints-field-label">
            {t(`${TRANSLATION_ROOT}.installation-key-label`)}
          </span>

          <div className="endpoints-installer-row">
            {key ? (
              <div className="endpoints-key-display">
                <span className="endpoints-key-expiry">
                  {t(`${TRANSLATION_ROOT}.expiration-label`, {
                    date: formatDateInUtcWithTz(installationKey.data?.expirationDate),
                  })}
                </span>
                <span className="endpoints-masked-key">{maskKey(key)}</span>
              </div>
            ) : (
              <span />
            )}
            <Button
              variant="primary"
              size="sm"
              icon={<KeyRound size={16} />}
              loading={installationKey.isPending}
              onClick={() => installationKey.mutate()}
            >
              {t(`${TRANSLATION_ROOT}.generate-key`)}
            </Button>
          </div>

          {installationKey.isError && (
            <p className="endpoints-drawer-error">{t(`${TRANSLATION_ROOT}.key-error`)}</p>
          )}

          {key && (
            <div className="endpoints-installer-row">
              <span>{t(`${TRANSLATION_ROOT}.copy-key-label`)}</span>
              <Button
                variant="default"
                size="sm"
                icon={<Copy size={16} />}
                onClick={() => copy(key, `${TRANSLATION_ROOT}.copy-key-success`)}
              >
                {t(`${TRANSLATION_ROOT}.copy-key`)}
              </Button>
            </div>
          )}
        </div>

        {key && (
          <div className="endpoints-installer-row endpoints-installer-command">
            <span>{t(`${TRANSLATION_ROOT}.copy-command-label`)}</span>
            <Button
              variant="default"
              size="sm"
              icon={<Copy size={16} />}
              onClick={() =>
                copy(installCommandLine(key, version), `${TRANSLATION_ROOT}.copy-command-success`)
              }
            >
              {t(`${TRANSLATION_ROOT}.copy-command`)}
            </Button>
          </div>
        )}
      </Drawer.Panel>
    </Drawer>
  );
};
